// Where the keyboard focus is when a box closes by itself: a box that
// closes on its save's answer (a composer, a reply, an editor) closes at a
// moment the person did not choose, so it may hand the focus on only if
// the person is still in it.

/** The focus is in the box, on nothing (the page's body: e.g. the box was just removed), or in something else the person moved to. */
export type FocusAt = 'box' | 'nowhere' | 'elsewhere';

export function focusAt(box: Element | null | undefined, doc: Document = document): FocusAt {
  const active = doc.activeElement;
  if (!active || active === doc.body || active === doc.documentElement) return 'nowhere';
  return box?.contains(active) ? 'box' : 'elsewhere';
}

/** A box closing on its save's answer hands the focus on (to the reader, to its Reply button) unless the person has put it elsewhere. */
export const handsFocusOn = (at: FocusAt): boolean => at !== 'elsewhere';

/** The lines a composer is on, and a selection of lines, as far as `afterSaved` reads them. */
type ComposerLines = { path: string; side: 'LEFT' | 'RIGHT'; startLine: number; line: number };
type SelectedLines = { path: string; side: 'LEFT' | 'RIGHT'; start: number; end: number };

/**
 * What the Files view does when its composer's draft is saved and takes
 * its place. That happens when the server answers, not at a press. With
 * the focus still in the composer, or nowhere, it is as closing it by
 * hand: the reader takes the focus, and the selection and the range being
 * made go. With the focus elsewhere the person keeps it, with whatever
 * lines they have selected since; only a selection that is still the
 * composer's own lines goes with it.
 */
export function afterSaved<S extends SelectedLines>(focus: FocusAt, composer: ComposerLines | null, selection: S | null): { focusReader: boolean; selection: S | null; clearRange: boolean } {
  if (handsFocusOn(focus)) return { focusReader: true, selection: null, clearRange: true };
  const own = !!composer && !!selection && selection.path === composer.path && selection.side === composer.side && selection.start === composer.startLine && selection.end === composer.line;
  return { focusReader: false, selection: own ? null : selection, clearRange: false };
}
